// swarm-ghosts.ts — visual swarm indicator for creature thumbnails.
//
// Renders semi-transparent "ghost" copies of the leader's portrait drifting
// inside the token wrapper. Ghost count = baseGhosts(tier) * (v_current/v_max),
// so a near-dead swarm visibly thins out. Pure CSS animation — no JS RAF loop.
//
// Two entry points:
//   buildSwarmGhostOverlayHTML(creature, picUrl) → string (initial paint)
//   syncSwarmGhosts(creatureEl, entity)          → void   (post-damage diff)
//
// Both attach to `window` so `renderCreatureThumbnail` (now a sibling in this
// bundle, creature-thumbnail.ts, since XZ closure Phase A 2026-06-13) can call
// buildSwarmGhostOverlayHTML and `animateEntityChanges` (this bundle) can call
// syncSwarmGhosts. Non-swarm creatures emit zero DOM nodes — feature is fully additive.

import type { EntityRow, EncounterCreatureRow } from './types';
import type { EncounterCreatureRow as SharedEncounterCreatureRow } from '../shared/array-types';
import { encounterCreatureField } from '../shared/array-types';
import {
    getSwarmTemplateForCreature,
    swarmWidthLanesForCreature,
    swarmOccupiedWidthFt,
    swarmVRatio01,
    type SwarmTemplateDef,
    type SwarmTier,
} from '../shared/swarm-templates';

const GHOST_OVERLAY_CLASS = 'swarm-ghost-overlay';
const GHOST_CLASS         = 'swarm-ghost';

function computeGhostCount(def: SwarmTemplateDef, vRatio01: number): number {
    if (!Number.isFinite(vRatio01) || vRatio01 <= 0) return 0;
    const clamped = vRatio01 > 1 ? 1 : vRatio01;
    return Math.max(0, Math.round(def.baseGhosts * clamped));
}

// Ratio source is the shared swarmVRatio01 (v_current / effective_v_max) — NOT
// v_bar_v_percent, whose denominator includes temp vitality (a temp-V buff
// would wrongly thin the horde). Same ratio drives the occupied-width rule.
function readVRatio01(entity: EntityRow): number {
    return swarmVRatio01(entity as unknown as SharedEncounterCreatureRow);
}

function asCreatureIfSwarm(
    entity: EntityRow,
): { creature: EncounterCreatureRow; def: SwarmTemplateDef & { tier: SwarmTier } } | null {
    const c = entity as EncounterCreatureRow;
    if (!encounterCreatureField(c.encounter_creatures, 'encounter_creature_id')) return null;
    const def = getSwarmTemplateForCreature(c);
    if (!def) return null;
    return { creature: c, def };
}

function escapeAttr(s: string): string {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function renderGhostImg(i: number, total: number, picUrl: string): string {
    // Horizontal horde band (2026-07-13, was a center ring): ghosts STAND ON THE
    // GROUND (bottom-anchored) and fan out symmetrically from a leader-clear
    // center slot toward the token edges — the widened swarm box reads as a
    // left-to-right horde, not a tower. Outermost ghosts sit nearest the edge
    // fade (overlay mask). Per-ghost height/opacity/timing pseudo-random by
    // index keeps depth variation stable across re-renders without state.
    const side  = i % 2 === 0 ? 1 : -1;            // alternate right/left of the leader
    const rank  = Math.floor(i / 2) + 1;           // 1..ranks, marching outward
    const ranks = Math.max(1, Math.ceil(total / 2));
    const xPct  = 50 + side * (16 + (rank / ranks) * 28);   // leader-clear ±16%, edge ~±44%

    const hPct  = 62 + ((i * 9) % 15);             // 62–76% of leader height → depth read
    const alpha = (0.72 - (rank / ranks) * 0.16).toFixed(2); // solid-ish, fainter toward the edges
    const dur   = (3.2 + ((i * 1.37) % 2.4)).toFixed(2);
    const delay = ((i * 0.93) % 3).toFixed(2);
    const dir   = i % 2 === 0 ? 'normal' : 'reverse'; // neighbors sway opposite ways

    const safeUrl = escapeAttr(picUrl);
    return `<img class="${GHOST_CLASS}" data-i="${i}" src="${safeUrl}" alt="" `
         + `style="bottom:0;left:${xPct.toFixed(2)}%;height:${hPct}%;opacity:${alpha};`
         + `animation-duration:${dur}s;animation-delay:${delay}s;animation-direction:${dir};">`;
}

function buildSwarmGhostOverlayHTML(creature: EntityRow, picUrl: string | null | undefined): string {
    const match = asCreatureIfSwarm(creature);
    if (!match || !picUrl) return '';

    const count = computeGhostCount(match.def, readVRatio01(creature));
    if (count <= 0) return '';

    let ghosts = '';
    for (let i = 0; i < count; i++) {
        ghosts += renderGhostImg(i, count, picUrl);
    }

    return `<div class="${GHOST_OVERLAY_CLASS} position-absolute top-0 start-0 w-100 h-100 pointer-events-none" `
         + `data-swarm-tier="${match.def.tier}" style="z-index:1;">`
         + ghosts
         + `</div>`;
}

/**
 * Post-damage width resync (2026-07-13): the occupied width is a function of
 * remaining vitality, so a hit that crosses a lane threshold must narrow the
 * token box without waiting for a full rebuild. Also resets the box back to
 * base width when the entity STOPS being a swarm (def = null — template
 * removed / shapeshift). Keeps the token CENTERED on its x (left compensates
 * by half the rendered-pixel delta — XZPositioning computed left as
 * x - width/2 at paint). Snap, no width transition: stationary creatures carry
 * `transition:none !important`, and AOE origin math reads live offsetWidth.
 *
 * ZOOM: an existing inline width may include a zoom scale, so the change gate compares UNZOOMED dataset
 * values and the rendered scale is recovered as prevStyleW / prevDatasetW —
 * never write an unzoomed width over a zoomed one. Skipped mid-drag/move; the
 * drop path repaints from the datasets afterwards.
 */
function syncSwarmTokenWidth(creatureEl: HTMLElement, entity: EntityRow, def: SwarmTemplateDef | null): void {
    if (creatureEl.classList.contains('moving') || creatureEl.classList.contains('being-dragged')) return;
    const sizeInfo = SIZE_TYPES.find((s) => s.variable_name === (creatureEl.dataset.size ?? ''));
    if (!sizeInfo) return;
    const baseFt = sizeInfo.base_size;
    const thumbW = sizeInfo.thumbnail_width;

    const shared = entity as unknown as SharedEncounterCreatureRow;
    const occFt = def
        ? swarmOccupiedWidthFt(baseFt, def, swarmWidthLanesForCreature(shared, def), swarmVRatio01(shared))
        : baseFt;
    const tokenW = Math.round(thumbW * (occFt / baseFt));

    const prevDataW = parseFloat(creatureEl.dataset.thumbnail_width ?? '');
    const prevW = parseFloat(creatureEl.style.width);
    if (!Number.isFinite(prevDataW) || prevDataW <= 0 || !Number.isFinite(prevW) || prevW <= 0) return;
    if (Math.round(prevDataW) === tokenW) return; // unzoomed compare — already in sync

    const zoomScale = prevW / prevDataW;
    const newStyleW = tokenW * zoomScale;

    creatureEl.style.width = `${newStyleW}px`;
    creatureEl.dataset.thumbnail_width = String(tokenW);
    creatureEl.dataset.width_ft = String(occFt);
    const left = parseFloat(creatureEl.style.left);
    if (Number.isFinite(left)) {
        creatureEl.style.left = `${left + (prevW - newStyleW) / 2}px`;
    }
    const wrapper = creatureEl.querySelector<HTMLElement>('.position-relative');
    wrapper?.style.setProperty('--tw', `${tokenW}px`);
}

function syncSwarmGhosts(creatureEl: HTMLElement, entity: EntityRow): void {
    const insertParent = creatureEl.querySelector<HTMLElement>('.position-relative') ?? creatureEl;
    const existingOverlay = insertParent.querySelector<HTMLElement>(`.${GHOST_OVERLAY_CLASS}`);
    const match = asCreatureIfSwarm(entity);

    if (!match) {
        if (existingOverlay) existingOverlay.remove();
        syncSwarmTokenWidth(creatureEl, entity, null); // swarm template removed → base width
        return;
    }

    syncSwarmTokenWidth(creatureEl, entity, match.def);

    const desired = computeGhostCount(match.def, readVRatio01(entity));
    if (desired <= 0) {
        if (existingOverlay) existingOverlay.remove();
        return;
    }

    // Reuse the live leader portrait's src so we never desync from
    // creature-portrait swaps already handled by media-signature.
    const leader = insertParent.querySelector<HTMLImageElement>('img.ft3d-token-media')
                ?? insertParent.querySelector<HTMLImageElement>('img');
    const picUrl = leader?.src ?? '';
    if (!picUrl) {
        if (existingOverlay) existingOverlay.remove();
        return;
    }

    const currentCount = existingOverlay
        ? existingOverlay.querySelectorAll(`img.${GHOST_CLASS}`).length
        : 0;

    if (existingOverlay
        && currentCount === desired
        && existingOverlay.dataset.swarmTier === match.def.tier
    ) {
        return; // Already in sync
    }

    // Rebuild on count or tier change. At desired ≤ 8 the DOM churn is
    // negligible, and a full rebuild keeps the evenly-distributed angle
    // pattern correct (positions depend on `i/total`).
    if (existingOverlay) existingOverlay.remove();
    insertParent.insertAdjacentHTML('beforeend', buildSwarmGhostOverlayHTML(entity, picUrl));
}

(window as unknown as { buildSwarmGhostOverlayHTML: typeof buildSwarmGhostOverlayHTML }).buildSwarmGhostOverlayHTML = buildSwarmGhostOverlayHTML;
(window as unknown as { syncSwarmGhosts: typeof syncSwarmGhosts }).syncSwarmGhosts = syncSwarmGhosts;

export { buildSwarmGhostOverlayHTML, syncSwarmGhosts };
